import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { Modal, Button } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";
import StyledSelect from "../../components/StyledSelect";
import "../../styles/AssignTasksPage.css";

const parseJsonSafe = (raw) => {
  try {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") return JSON.parse(raw);
    if (typeof raw === "number") return [raw];
    if (typeof raw === "object")
      return Array.isArray(raw) ? raw : Object.values(raw);
    return [];
  } catch {
    return [];
  }
};

const getCurrentUserEmail = (token) => {
  try {
    const me = JSON.parse(localStorage.getItem("me") || "{}");
    if (me?.email) return String(me.email).trim().toLowerCase();
    const encoded = String(token || "").split(".")[1] || "";
    const payload = JSON.parse(
      window.atob(encoded.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return String(payload?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const AssignTasksPage = ({ userId }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // Data
  const [categories, setCategories] = useState([]);
  const [allTasks, setAllTasks] = useState([]); // used globally
  const [modalTasks, setModalTasks] = useState([]); // used in edit modal
  const [programs, setPrograms] = useState([]);

  // Selections
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [programManagedTaskIds, setProgramManagedTaskIds] = useState([]);

  // Edit modal
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingTaskIds, setEditingTaskIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [positionCategories, setPositionCategories] = useState([]);
  const [editingCategoryIds, setEditingCategoryIds] = useState([]);

  // Filters (bottom table)
  const [filterCategory, setFilterCategory] = useState("");
  const [q, setQ] = useState("");
  const [programQuery, setProgramQuery] = useState("");
  const [programPage, setProgramPage] = useState(1);

  // Pagination (match AssignTrainingPage style)
  const [page, setPage] = useState(1);
  const perPage = 12;
  const programPerPage = 8;
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [isSyncingPrograms, setIsSyncingPrograms] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [isCancelingSync, setIsCancelingSync] = useState(false);
  const syncPollTimerRef = useRef(null);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    tasks: {
      assign: hasPermission("Task", "Task Assignment", "edit"),
      edit: hasPermission("Task", "Task Assignment", "edit"),
    },
  };
  const canRunTaskProgramSync =
    getCurrentUserEmail(token) === "daniel.chow@newwaytrucking.com";
  // Load initial data
  useEffect(() => {
    const load = async () => {
      const [cats, tasks, progs, assigned, user] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/categories`, { headers }),
        axios.get(`${BASE_URL}/api/tasks`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/programs`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/assigned/${userId}?includeHidden=1`, {
          headers,
        }),
        axios.get(`${BASE_URL}/api/users/${userId}`, { headers }),
      ]);

      setCategories(cats.data || []);
      setAllTasks(tasks.data || []);
      setPrograms(progs.data || []);

      // Assigned tasks -> start state
      const assignedTaskIds = (assigned.data?.tasks || [])
        .filter((t) => Number(t.uat_visible ?? 1) === 1)
        .map((t) => Number(t.t_id));
      setSelectedTaskIds(assignedTaskIds);
      setProgramManagedTaskIds(
        (assigned.data?.tasks || [])
          .filter((t) => t.uat_source === "program")
          .map((t) => Number(t.t_id))
          .filter(Number.isFinite)
      );

      // Stored program selection on user (JSON)
      const userPrograms = parseJsonSafe(user.data?.task_programs || []);
      setSelectedProgramIds(
        userPrograms.map(Number).filter(Number.isFinite)
      );
    };

    load().catch(console.error);
  }, [BASE_URL, headers, userId]);

  // A program definition is applied only when an administrator toggles that
  // program here. Editing a program never silently changes an existing user.
  const toggleProgram = (progId) => {
    const isOn = selectedProgramIds.includes(progId);
    const nextProgramIds = isOn
      ? selectedProgramIds.filter((id) => id !== progId)
      : [...selectedProgramIds, progId];
    const nextProgramTaskIds = Array.from(
      new Set(
        programs
          .filter((program) => nextProgramIds.includes(program.id))
          .flatMap((program) => program.taskIds)
          .map(Number)
          .filter(Number.isFinite)
      )
    );

    setSelectedProgramIds(nextProgramIds);
    setSelectedTaskIds((previous) => [
      ...previous.filter((id) => !programManagedTaskIds.includes(id)),
      ...nextProgramTaskIds.filter(
        (id) =>
          !previous
            .filter((taskId) => !programManagedTaskIds.includes(taskId))
            .includes(id)
      ),
    ]);
    setProgramManagedTaskIds(nextProgramTaskIds);
  };

  // Toggle an individual task (bottom table)
  const toggleTask = (id) =>
    setSelectedTaskIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Open edit modal for a program
  const openEditModal = async (program) => {
    setEditingProgram(program);
    setEditingTaskIds(program.taskIds);
    setEditingCategoryIds(program.rawCategory || []);

    try {
      const [tasksRes, catsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/editlist`, {
          headers,
          params: { programName: program.name },
        }),
        axios.get(`${BASE_URL}/api/tasks/positioncategories`, { headers }),
      ]);
      setModalTasks(tasksRes.data || []);
      setPositionCategories(catsRes.data || []);
    } catch (err) {
      console.error("❌ Failed to load edit modal data", err);
      setModalTasks([]);
      setPositionCategories([]);
    }

    setShowModal(true);
  };

  // Save edited tasks for a program
  const saveEditedTasks = async () => {
    if (!editingProgram) return;

    try {
      await axios.put(
        `${BASE_URL}/api/tasks/programs/${editingProgram.id}`,
        { taskIds: editingTaskIds, categoryIds: editingCategoryIds },
        { headers }
      );

      // 🧠 Find category names to display (convert IDs to labels)
      const updatedCategoryNames = positionCategories
        .filter((c) => editingCategoryIds.includes(c.c_id))
        .map((c) => c.c_name);

      // 🧩 Update frontend immediately (no refresh needed)
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === editingProgram.id
            ? {
                ...p,
                taskIds: editingTaskIds,
                category: updatedCategoryNames,
                rawCategory: editingCategoryIds,
              }
            : p
        )
      );

      setShowModal(false);
      alert("✔️ Program tasks & categories updated!");
    } catch (err) {
      console.error("❌ Failed to save edited tasks", err);
      alert("⚠️ Failed to save changes. Please try again.");
    }
  };

  // One transaction saves the user's explicit program and task selections.
  const handleSave = async () => {
    await axios.post(
      `${BASE_URL}/api/tasks/assign`,
      { userId, taskIds: selectedTaskIds, programIds: selectedProgramIds },
      { headers }
    );

    alert("✔️ Saved task programs & assignments.");
  };

  const handleSaveWithLoading = async () => {
    if (isSavingAssignments) return;

    try {
      setIsSavingAssignments(true);
      await handleSave();
    } catch (err) {
      console.error("Failed to save task assignments", err);
      alert("Failed to save task assignments. Please try again.");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  const pollSyncJob = (jobId) => {
    clearTimeout(syncPollTimerRef.current);
    syncPollTimerRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/tasks/programs/sync-all/${jobId}`,
          { headers }
        );
        const nextJob = response.data?.job;
        setSyncJob(nextJob);
        if (["queued", "running", "canceling"].includes(nextJob?.status)) {
          pollSyncJob(jobId);
          return;
        }
        setIsSyncingPrograms(false);
        setIsCancelingSync(false);
        if (nextJob?.status === "complete") {
          alert(
            `Sync complete. Updated ${Number(nextJob.syncedUsers || 0)} users without deleting task history.`
          );
        } else if (nextJob?.status === "failed") {
          alert(nextJob.error || "Sync failed.");
        }
      } catch (err) {
        console.error("Failed to check task program sync", err);
        setIsSyncingPrograms(false);
        setIsCancelingSync(false);
        alert("Could not check sync progress. Please refresh and try again.");
      }
    }, 1000);
  };

  const handleSyncExistingUsers = async () => {
    if (isSyncingPrograms) return;
    try {
      setIsSyncingPrograms(true);
      setIsCancelingSync(false);
      setSyncJob({ progress: 1, message: "Starting safe task sync..." });
      const response = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all`,
        {},
        { headers }
      );
      const jobId = response.data?.jobId;
      if (!jobId) throw new Error("Sync job was not created.");
      setSyncJob(response.data?.job);
      pollSyncJob(jobId);
    } catch (err) {
      console.error("Failed to start task program sync", err);
      setIsSyncingPrograms(false);
      setSyncJob(null);
      alert(
        err?.response?.data?.message ||
          "Failed to start task program sync. Please try again."
      );
    }
  };

  const handleCancelSync = async () => {
    if (!syncJob?.id || isCancelingSync) return;
    try {
      setIsCancelingSync(true);
      const response = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all/${syncJob.id}/cancel`,
        {},
        { headers }
      );
      setSyncJob(response.data?.job || syncJob);
    } catch (err) {
      console.error("Failed to cancel task program sync", err);
      setIsCancelingSync(false);
    }
  };

  useEffect(
    () => () => {
      clearTimeout(syncPollTimerRef.current);
    },
    []
  );

  const filteredPrograms = useMemo(() => {
    const term = programQuery.trim().toLowerCase();
    if (!term) return programs;

    return programs.filter((program) => {
      const name = String(program.name || "").toLowerCase();
      const categories = Array.isArray(program.category)
        ? program.category.join(", ").toLowerCase()
        : "";
      return name.includes(term) || categories.includes(term);
    });
  }, [programs, programQuery]);

  const pagedPrograms = useMemo(() => {
    const start = (programPage - 1) * programPerPage;
    return filteredPrograms.slice(start, start + programPerPage);
  }, [filteredPrograms, programPage]);

  // Filtering + paging
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allTasks.filter((t) => {
      const catOK =
        !filterCategory || String(t.t_category_id) === String(filterCategory);
      if (!term) return catOK;
      const title = String(t.t_title || "").toLowerCase();
      const catName = String(t.category_name || "").toLowerCase();
      return catOK && (title.includes(term) || catName.includes(term));
    });
  }, [allTasks, q, filterCategory]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page]);

  useEffect(() => setPage(1), [q, filterCategory]);
  useEffect(() => setProgramPage(1), [programQuery]);
  useEffect(
    () => () => {
      if (syncPollTimerRef.current) {
        clearTimeout(syncPollTimerRef.current);
      }
    },
    []
  );

  // UI helpers
  // const taskTitleById = useMemo(() => {
  //   const m = new Map();
  //   allTasks.forEach((t) => m.set(t.t_id, t.t_title));
  //   return m;
  // }, [allTasks]);

  return (
    <div className="container py-3 assign-tasks-page">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0 text-center flex-grow-1">Assign Tasks</h3>
      </div>

      {/* Programs (top) */}
      <div className="assign-tasks-section-header mt-3 mb-2">
        <h5 className="m-0">Program Assignment List</h5>
        <div className="nw-filter-bar assign-tasks-filters assign-tasks-program-filters">
          <input
            className="form-control"
            placeholder="Search program or category"
            value={programQuery}
            onChange={(e) => setProgramQuery(e.target.value)}
          />
          {canRunTaskProgramSync && (
            <button
              className="btn btn-outline-primary"
              onClick={handleSyncExistingUsers}
              disabled={isSyncingPrograms}
            >
              {isSyncingPrograms ? "Syncing..." : "Sync"}
            </button>
          )}
        </div>
      </div>
      <div className="table-responsive mb-4">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Program Name</th>
              <th>Employee Category</th>
              <th className="text-center assignment-action-column">Edit Program Tasks</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedPrograms.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  {filteredPrograms.length
                    ? "No task programs on this page."
                    : "No task programs match your search."}
                </td>
              </tr>
            ) : (
              pagedPrograms.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    {Array.isArray(p.category) ? p.category.join(", ") : ""}
                  </td>
                  <td className="text-center assignment-action-column">
                    <div className="assignment-action-group">
                      <button
                        type="button"
                        className="app-table-action-button is-edit"
                        onClick={() => openEditModal(p)}
                        disabled={!PERMISSIONS.tasks.edit}
                        title="Edit program tasks"
                        aria-label={`Edit tasks for ${p.name}`}
                      >
                        <i className="fas fa-edit" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedProgramIds.includes(p.id)}
                      onChange={() => toggleProgram(p.id)}
                      disabled={!PERMISSIONS.tasks.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={programPage}
          totalItems={filteredPrograms.length}
          itemsPerPage={programPerPage}
          onPageChange={setProgramPage}
        />
      </div>

      {/* Tasks (bottom) */}
      <div className="assign-tasks-section-header mb-2">
        <h5 className="m-0">User Task Assignment</h5>
        <div className="nw-filter-bar assign-tasks-filters assign-tasks-user-filters">
          <StyledSelect
            value={filterCategory}
            onChange={setFilterCategory}
            options={[
              { value: "", label: "All Categories" },
              ...categories.map((category) => ({
                value: category.tc_id,
                label: category.tc_name,
              })),
            ]}
            minWidth={180}
            ariaLabel="Filter tasks by category"
          />
          <input
            className="form-control"
            placeholder="Search task or category"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Task</th>
              <th>Category</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  No tasks match your filters.
                </td>
              </tr>
            ) : (
              paged.map((t) => (
                <tr key={t.t_id}>
                  <td>{t.t_title}</td>
                  <td>{t.category_name}</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.includes(t.t_id)}
                      onChange={() => toggleTask(t.t_id)}
                      disabled={!PERMISSIONS.tasks.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          totalItems={filtered.length}
          itemsPerPage={perPage}
          onPageChange={setPage}
        />
      </div>

      <div className="d-flex justify-content-end pt-3">
        {PERMISSIONS.tasks.assign && (
          <button
            className="btn btn-primary"
            onClick={handleSaveWithLoading}
            disabled={isSavingAssignments}
          >
            {isSavingAssignments ? "Saving..." : "Save Assignments"}
          </button>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Included Tasks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProgram ? (
            <>
              <p className="text-muted">
                Editing: <strong>{editingProgram.name}</strong>
              </p>
              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Employee Categories
                </label>
                <Select
                  isMulti
                  value={positionCategories
                    .filter((c) => editingCategoryIds.includes(c.c_id))
                    .map((c) => ({ value: c.c_id, label: c.c_name }))}
                  onChange={(selected) =>
                    setEditingCategoryIds((selected || []).map((s) => s.value))
                  }
                  options={positionCategories.map((c) => ({
                    value: c.c_id,
                    label: c.c_name,
                  }))}
                  placeholder="Search and select categories..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Tasks to Include
                </label>
                <Select
                  isMulti
                  value={modalTasks
                    .filter((t) => editingTaskIds.includes(t.t_id))
                    .map((t) => ({ value: t.t_id, label: t.t_title }))}
                  onChange={(selected) =>
                    setEditingTaskIds((selected || []).map((s) => s.value))
                  }
                  options={modalTasks.map((t) => ({
                    value: t.t_id,
                    label: t.t_title,
                  }))}
                  placeholder="Search and select tasks..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </>
          ) : (
            <p>No program selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditedTasks}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isSyncingPrograms} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Syncing Task Programs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {syncJob?.message || "Updating user tasks from current program lists..."}
          </p>
          <div className="progress" style={{ height: 18 }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${Math.max(1, Number(syncJob?.progress || 1))}%` }}
              aria-valuenow={Number(syncJob?.progress || 1)}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {Math.round(Number(syncJob?.progress || 1))}%
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleCancelSync}
            disabled={isCancelingSync || syncJob?.status === "canceling"}
          >
            {isCancelingSync || syncJob?.status === "canceling"
              ? "Canceling..."
              : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignTasksPage;
